/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsUpdate } from "../funcs/skillsUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Skill } from "../models/components/skill.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateSkillRequest,
  UpdateSkillSecurity,
} from "../models/operations/updateskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateSkillMutationVariables = {
  request: UpdateSkillRequest;
  security?: UpdateSkillSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateSkillMutationData = Skill;

export type UpdateSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * update skills
 *
 * @remarks
 * Rename an active skill or update its display name, summary, and tags. The implementation requires the skills product feature and skill write scope.
 */
export function useUpdateSkillMutation(
  options?: MutationHookOptions<
    UpdateSkillMutationData,
    UpdateSkillMutationError,
    UpdateSkillMutationVariables
  >,
): UseMutationResult<
  UpdateSkillMutationData,
  UpdateSkillMutationError,
  UpdateSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateSkill(): MutationKey {
  return ["@gram/client", "skills", "update"];
}

export function buildUpdateSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateSkillMutationVariables,
  ) => Promise<UpdateSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateSkill(),
    mutationFn: function updateSkillMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
